<template>
  <Content
    :header-label="t('domainPopulator.title')"
    :subheader="t('domainPopulator.description')"
    class="domain-populator"
    show-toolbar
  >
    <template #wizbar>
      <Message v-if="response" severity="success" class="rygen-message">
        <div class="domain-success-content">
          <div class="mb-2">
            <strong>{{ t('domainPopulator.domainCreatedSuccess') }}</strong>
          </div>

          <div class="mb-2">
            <strong>{{ t('domainPopulator.domainUrl') }}:</strong>
            <a
              :href="response.domainUrl"
              target="_blank"
              class="ml-2 text-primary external-link"
            >
              {{ response.domainUrl }}
              <i aria-hidden="true" class="pi pi-external-link ml-1" />
            </a>
          </div>

          <div>
            <strong>{{ t('domainPopulator.userCredentials') }}:</strong>
            <div class="credentials-list mt-1">
              <div
                v-for="cred in response.userCredentials"
                :key="cred.username"
                class="credential-item p-2 border-round surface-ground mt-1"
              >
                <div class="flex align-items-center justify-content-between">
                  <span class="font-semibold">
                    {{ cred.displayName }} ({{ cred.role }})
                  </span>
                </div>
                <div class="mt-1">
                  <div class="mb-1">
                    <span class="text-sm">
                      {{ t('domainPopulator.username') }}:
                    </span>
                    <code
                      class="bg-gray-100 px-1 py-0 border-round text-sm ml-1"
                    >
                      {{ cred.username }}
                    </code>
                  </div>
                  <div>
                    <span class="text-sm">
                      {{ t('domainPopulator.password') }}:
                    </span>
                    <code
                      class="bg-gray-100 px-1 py-0 border-round text-sm ml-1"
                    >
                      {{ cred.password }}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Message>
    </template>

    <form class="flex flex-column flex-gap-1" @submit.prevent="handleSubmit">
      <!-- Client Name Field -->
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="client-name"
        :label="t('domainPopulator.clientName')"
        :errors="v$.clientName.$errors"
      >
        <InputText
          :id="inputId"
          v-model="form.clientName"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :placeholder="t('domainPopulator.clientNamePlaceholder')"
        />
      </InputWrapper>

      <!-- Branding Toggle -->
      <WrappedSwitch
        id="enable-branding"
        v-model="enableBrandingUI"
        :label="t('domainPopulator.enableBranding')"
      />

      <!-- Branding Editor (conditionally shown) -->
      <Transition name="slide-fade">
        <div v-if="enableBrandingUI" class="branding-section">
          <BrandingSection
            v-model="currentDomainBranding"
            :domain="currentDomain"
            :show-actions="false"
            :auto-save="false"
            @has-pending-uploads="onHasPendingUploads"
          />
        </div>
      </Transition>
    </form>

    <template #toolbar>
      <div class="toolbar">
        <RygenButton
          mode="primary"
          icon="pi pi-plus"
          :loading="isSubmitting"
          :disabled="!isFormValid"
          :label="t('domainPopulator.createDomain')"
          juicy
          @click="handleSubmit"
        />
      </div>
    </template>
  </Content>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, onMounted, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import {
  required,
  useToast,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { DomainBranding, DomainReference } from '@rygen/rygen-ui/model';

import api from '@/api';
import { DomainPopulatorRequest, DomainPopulatorResponse } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import BrandingSection from '@/views/domainpopulator/BrandingSection.vue';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';

const { t } = useI18n();
const toast = useToast();
const route = useRoute();
const router = useRouter();
const { setSelectedDomainId } = useCurrentUserStore();

const form: Ref<DomainPopulatorRequest> = ref(new DomainPopulatorRequest());

const response: Ref<DomainPopulatorResponse | undefined> = ref();

const rules = computed(() => ({
  clientName: {
    required: required(t('domainPopulator.clientName')),
  },
}));

const { uid } = useUid('domain-populator');
const { v$, validate } = useValidations(form, rules, uid);

const isSubmitting = ref(false);
const enableBrandingUI = ref(false);
const hasPendingUploads = ref(false);

// Track the created domain for branding uploads
const createdDomainId = ref<number | null>(null);

const currentDomain = computed(() => {
  // Use the created domain ID if available, otherwise fall back to form data
  const domainId = createdDomainId.value || form.value.domainBranding?.domainId;
  return new DomainReference({ id: domainId });
});

const currentDomainBranding = computed({
  get: () => {
    const branding = form.value.domainBranding || new DomainBranding();
    // Ensure the domain ID is set for branding uploads
    if (createdDomainId.value) {
      branding.domainId = createdDomainId.value;
    }
    return branding;
  },
  set: (newBranding: DomainBranding) => {
    form.value.domainBranding = newBranding;
  },
});

// Check if form is valid
const isFormValid = computed(() => !v$.value.$invalid);

const onHasPendingUploads = (hasPending: boolean): void => {
  hasPendingUploads.value = hasPending;
};

const resetForm = (): void => {
  form.value = new DomainPopulatorRequest();
  enableBrandingUI.value = false;
  createdDomainId.value = null;
  hasPendingUploads.value = false;
  v$.value.$reset();
};

const showError = (error: any): void => {
  toast.addError({
    error,
    detail: error.message || t('domainPopulator.domainCreationFailed'),
  });
};

const handleSubmit = async (): Promise<void> => {
  if (!form.value.clientName) {
    return;
  }
  form.value.clientName = form.value.clientName.trim();

  const isValid = await validate();
  if (!isValid) {
    return;
  }

  isSubmitting.value = true;

  try {
    const requestData = {
      ...form.value,
      domainBranding:
        enableBrandingUI.value && form.value.domainBranding
          ? new DomainBranding({
              accentColor: form.value.domainBranding.accentColor,
            })
          : undefined,
    };

    // Save the response when we make the network call
    response.value =
      await api.domainPopulator.createDomainAndUsers(requestData);

    // Store the created domain ID for branding uploads
    createdDomainId.value = response.value.domainId ?? null;

    // The toast can be a simple success message. We can render details in the template
    toast.addSuccess({
      detail: t('domainPopulator.domainCreatedSuccess'),
    });

    // Continue with the remaining population steps in the background
    if (response.value.domainId && form.value.domainType) {
      api.domainPopulator
        .continuePopulation(response.value.domainId, form.value.domainType)
        .catch((error) => {
          console.error('Error continuing population:', error);
          toast.addError({
            error,
            detail:
              'Domain and users created successfully, but some additional setup failed.',
          });
        });
    }

    // The BrandingSection will automatically handle uploads when the domain ID becomes available
    // and will emit events to notify us of the status

    // Only reset form after all uploads are complete
    if (!hasPendingUploads.value) {
      resetForm();
      isSubmitting.value = false;
    } else if (!enableBrandingUI.value) {
      isSubmitting.value = false;
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    showError(e);
    isSubmitting.value = false;
  }
};

// Watch for upload completion to reset form
watch(hasPendingUploads, (hasPending) => {
  if (!hasPending && createdDomainId.value) {
    // All uploads completed, now we can reset
    setTimeout(() => {
      if (!hasPendingUploads.value) {
        resetForm();
        isSubmitting.value = false;
      }
    }, 100);
  }
});

watch(enableBrandingUI, (enabled) => {
  if (!enabled) {
    form.value.domainBranding = undefined;
  } else if (!form.value.domainBranding) {
    form.value.domainBranding = new DomainBranding();
  }
});

// Check for domain query parameter on mount and switch domains if provided
onMounted(() => {
  const domainParam = route.query.domain;
  if (domainParam && typeof domainParam === 'string') {
    const domainId = parseInt(domainParam, 10);
    if (!isNaN(domainId)) {
      setSelectedDomainId(domainId);
      // Remove the domain parameter from the URL after processing
      const newQuery = { ...route.query };
      delete newQuery.domain;
      router.replace({ query: newQuery });
    }
  }
});
</script>

<style>
.domain-populator {
  .checkbox-wrapper {
    display: flex;
    align-items: center;
  }

  .branding-section {
    margin-top: var(--gap-2);
  }

  .toolbar {
    display: flex;
    gap: var(--gap-1);
    justify-content: center;
    align-items: end;
    padding: var(--sp-3);
  }
}

/* Smooth transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
